import axios from 'axios';
import { BASE_URL } from './api';

// Conversations that started in Outlook and look like outreach, waiting on the
// mailbox's reviewers (its owner, or anyone who may send from a shared one) to
// bring in (approve) or leave out (reject). Only the mailboxes the user reviews
// are ever returned.

// params: { status, answered, q, mailbox, page }.
// Pending lists only conversations we wrote in unless answered says otherwise.
export async function getOutreachCandidates(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-thread-candidates/`,
        headers: { 'Authorization': `Token ${token}` },
        params,
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// { pending }: what the list shows by default, for the tab's badge.
export async function getOutreachCandidateCounts(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-thread-candidates/counts/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// The mailboxes whose candidates this user reviews, for the mailbox filter:
// [{ id, email_address, owner }].
export async function getOutreachCandidateMailboxes(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-thread-candidates/mailboxes/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// The conversation read live from Outlook: { messages, skipped,
// matching_companies }. 409 with thread_id when it's already a thread.
export async function getOutreachCandidatePreview(candidateId, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-thread-candidates/${candidateId}/preview/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Import it as a thread. companyId null takes the suggested company; the
// campaign is that company's current one. Returns { thread_id, recorded }.
export async function approveOutreachCandidate(candidateId, token, companyId = null) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-thread-candidates/${candidateId}/approve/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: companyId ? { company_id: companyId } : {},
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

export async function rejectOutreachCandidate(candidateId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-thread-candidates/${candidateId}/reject/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

export async function restoreOutreachCandidate(candidateId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-thread-candidates/${candidateId}/restore/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Reject (or restore) several at once. Returns { updated_ids }: the ones
// changed; any other id wasn't pending (rejected) any more and was left alone.
export async function bulkRejectOutreachCandidates(candidateIds, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-thread-candidates/bulk-reject/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { ids: candidateIds },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

export async function bulkRestoreOutreachCandidates(candidateIds, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-thread-candidates/bulk-restore/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { ids: candidateIds },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Companies by name or website: [{ id, company_name, website_url, campaign }].
export async function searchCompaniesForCandidate(token, q) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-thread-candidates/company-search/`,
        headers: { 'Authorization': `Token ${token}` },
        params: { q },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Never suggest anyone at this candidate's domain to its owner again, and reject
// their pending ones there. Returns { domain, ignored_domain_id, rejected_ids }.
export async function ignoreOutreachCandidateDomain(candidateId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-thread-candidates/${candidateId}/ignore-domain/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// [{ id, domain, owner, created_at }].
export async function getIgnoredOutreachDomains(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-ignored-domains/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Stop ignoring a domain. Conversations it rejected stay rejected.
export async function removeIgnoredOutreachDomain(ignoredDomainId, token) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/outreach-ignored-domains/${ignoredDomainId}/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Take back an ignore just made: remove it and restore the candidates it
// rejected (ignoreOutreachCandidateDomain's rejected_ids). Returns { restored }.
export async function undoIgnoredOutreachDomain(ignoredDomainId, candidateIds, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-ignored-domains/${ignoredDomainId}/undo/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { candidate_ids: candidateIds },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}
